import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { crearPaciente, listarEspecialidades } from "../../api";
import "./PacienteForm.css";

export default function PacienteForm() {
  const normCode = (c) => String(c ?? "").trim();

  const [form, setForm] = useState({
    nombre: "",
    fechaNacimiento: "",
    sexo: "",
    colegio: "",
    direccion: "",
    servicios: {}, // {code:boolean}
    observaciones: "",
    tutor: "",
    tutor_dni: "",
    telefono: "",
    email: "",
    emergencia: "",
  });

  const [especialidades, setEspecialidades] = useState([]);
  const [loadingSpecs, setLoadingSpecs] = useState(true);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const navigate = useNavigate();

  // ✅ Cargar especialidades desde la BD (ROBUSTO)
  useEffect(() => {
    (async () => {
      try {
        setLoadingSpecs(true);

        const r = await listarEspecialidades();

        // r puede ser: array | {items:[...]} | {ok:true,items:[...]}
        const items = Array.isArray(r)
          ? r
          : Array.isArray(r?.items)
          ? r.items
          : Array.isArray(r?.specialties)
          ? r.specialties
          : [];

        setEspecialidades(items);

        // inicializa servicios para checkboxes controlados (NORMALIZANDO code)
        setForm((prev) => {
          const servicios = { ...(prev.servicios || {}) };
          for (const esp of items) {
            const code = normCode(esp?.code);
            if (!code) continue;
            if (servicios[code] === undefined) servicios[code] = false;
          }
          return { ...prev, servicios };
        });
      } catch (e) {
        console.error("Error listando especialidades:", e);
        setMsg({
          type: "error",
          text: e.message || "Error al listar especialidades",
        });
        setEspecialidades([]);
      } finally {
        setLoadingSpecs(false);
      }
    })();
  }, []);

  // --- Manejo de cambios ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      const code = normCode(name);
      setForm((p) => ({
        ...p,
        servicios: { ...(p.servicios || {}), [code]: checked },
      }));
    } else {
      setForm((p) => ({ ...p, [name]: value }));
    }
  };

  // ✅ Conteo robusto: solo cuenta códigos que existan en especialidades
  const selectedCount = useMemo(() => {
    const s = form.servicios || {};
    const validCodes = new Set(
      (Array.isArray(especialidades) ? especialidades : []).map((e) =>
        normCode(e?.code)
      )
    );

    return Object.entries(s).filter(
      ([k, v]) => !!v && validCodes.has(normCode(k))
    ).length;
  }, [form.servicios, especialidades]);

  // --- Validaciones ---
  const validate = () => {
    if (!form.nombre.trim()) return "El nombre del paciente es obligatorio";
    if (!form.fechaNacimiento) return "La fecha de nacimiento es obligatoria";
    if (!form.sexo) return "Selecciona el sexo";

    if (selectedCount <= 0) return "Selecciona al menos una especialidad";

    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email))
      return "Email del tutor no válido";

    return null;
  };

  // --- Envío ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    const err = validate();
    if (err) return setMsg({ type: "error", text: err });

    setLoading(true);

    try {
      // 1) Códigos seleccionados (para UI / debug)
      const serviciosSeleccionados = Object.entries(form.servicios || {})
        .filter(([, v]) => v)
        .map(([code]) => normCode(code))
        .filter(Boolean);

      // 2) ✅ Lo que REALMENTE espera patients.create.php: IDs
      const codeToId = new Map(
        (Array.isArray(especialidades) ? especialidades : []).map((e) => [
          normCode(e?.code),
          e?.id,
        ])
      );

      const specialty_ids = serviciosSeleccionados
        .map((code) => codeToId.get(code))
        .filter((id) => id !== undefined && id !== null)
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);

      if (specialty_ids.length === 0) {
        // Esto no debería pasar si tus especialidades vienen bien,
        // pero así evitamos enviar vacío al backend.
        throw new Error(
          "No se pudieron resolver IDs de especialidad. Revisa que specialties.list.php devuelva {id, code}."
        );
      }

      const payload = {
        nombre: form.nombre.trim(),
        fechaNacimiento: form.fechaNacimiento,
        sexo: form.sexo,
        colegio: form.colegio.trim(),
        direccion: form.direccion.trim(),

        // ✅ CLAVE para tu backend
        specialty_ids,

        // (Opcional) mantener codes por si te sirve para debug
        servicios: serviciosSeleccionados,

        observaciones: form.observaciones.trim(),
        tutor: form.tutor.trim(),
        tutor_dni: form.tutor_dni.trim(),
        telefono: form.telefono.trim(),
        email: form.email.trim(),
        emergencia: form.emergencia.trim(),
      };

      // Debug útil si algo falla:
      // console.log("[PacienteForm] payload:", payload);

      const res = await crearPaciente(payload);

      setMsg({
        type: "ok",
        text: `✅ Paciente creado correctamente (ID ${
          res.patient_id || res.patient?.id || "?"
        })`,
      });

      // reset manteniendo checkboxes disponibles (robusto + NORMALIZADO)
      setForm((prev) => {
        const resetServicios = {};
        const specs = Array.isArray(especialidades) ? especialidades : [];
        for (const esp of specs) {
          const code = normCode(esp?.code);
          if (code) resetServicios[code] = false;
        }

        return {
          ...prev,
          nombre: "",
          fechaNacimiento: "",
          sexo: "",
          colegio: "",
          direccion: "",
          servicios: resetServicios,
          observaciones: "",
          tutor: "",
          tutor_dni: "",
          telefono: "",
          email: "",
          emergencia: "",
        };
      });

      // navigate("/admin/pacientes");
    } catch (e2) {
      console.error(e2);
      setMsg({
        type: "error",
        text: e2.message || "Error al crear paciente",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="paciente-form" onSubmit={handleSubmit}>
      <h2>Nuevo Paciente</h2>

      {msg.text && (
        <div
          className={`notice ${msg.type === "error" ? "error" : "ok"}`}
          role="alert"
        >
          {msg.text}
        </div>
      )}

      <label>
        Nombre completo del paciente
        <input
          type="text"
          name="nombre"
          value={form.nombre}
          onChange={handleChange}
          required
        />
      </label>

      <label>
        Fecha de nacimiento
        <input
          type="date"
          name="fechaNacimiento"
          value={form.fechaNacimiento}
          onChange={handleChange}
          required
        />
      </label>

      <label>
        Sexo
        <select name="sexo" value={form.sexo} onChange={handleChange} required>
          <option value="">Seleccione...</option>
          <option value="M">Masculino</option>
          <option value="F">Femenino</option>
          <option value="O">Otro</option>
        </select>
      </label>

      <label>
        Colegio
        <input
          type="text"
          name="colegio"
          value={form.colegio}
          onChange={handleChange}
        />
      </label>

      <label>
        Dirección
        <input
          type="text"
          name="direccion"
          value={form.direccion}
          onChange={handleChange}
          placeholder="Ej: Calle Mayor 10, Madrid"
        />
      </label>

      <fieldset>
        <legend>Servicios / Especialidades</legend>

        {loadingSpecs ? (
          <p>Cargando especialidades...</p>
        ) : !Array.isArray(especialidades) || especialidades.length === 0 ? (
          <div>
            <p>No hay especialidades disponibles.</p>
            <p>Seleccionadas: {selectedCount}</p>
          </div>
        ) : (
          <>
            {especialidades.map((esp) => {
              const code = normCode(esp?.code);
              return (
                <label key={code || esp.id}>
                  <input
                    type="checkbox"
                    name={code}
                    checked={!!form.servicios?.[code]}
                    onChange={handleChange}
                  />{" "}
                  {esp.nombre}
                </label>
              );
            })}
            <p style={{ marginTop: 6 }}>Seleccionadas: {selectedCount}</p>
          </>
        )}
      </fieldset>

      <label>
        Observaciones
        <textarea
          name="observaciones"
          value={form.observaciones}
          onChange={handleChange}
        />
      </label>

      <h3>Datos del tutor legal</h3>

      <label>
        Nombre completo del tutor
        <input
          type="text"
          name="tutor"
          value={form.tutor}
          onChange={handleChange}
        />
      </label>

      <label>
        DNI del tutor legal
        <input
          type="text"
          name="tutor_dni"
          value={form.tutor_dni}
          onChange={handleChange}
          placeholder="Ej: 87654321Z"
        />
      </label>

      <label>
        Teléfono del tutor
        <input
          type="tel"
          name="telefono"
          value={form.telefono}
          onChange={handleChange}
        />
      </label>

      <label>
        Email del tutor
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
        />
      </label>

      <label>
        Número de emergencia
        <input
          type="tel"
          name="emergencia"
          value={form.emergencia}
          onChange={handleChange}
        />
      </label>

      <button type="submit" disabled={loading || loadingSpecs}>
        {loading ? "Guardando..." : "Guardar Paciente"}
      </button>
    </form>
  );
}
